/**
 * Where the picture lives. Three homes and no fourth.
 *
 * The owner's rule, 2026-09-20: "no more mini player, no one uses it and it's
 * an unnecessary middle step and confusion". A video is in exactly one of full
 * screen, the guide's preview pane, or Android's picture-in-picture window —
 * and if it can be in none of them, it stops.
 *
 * WHY THIS IS A FUNCTION AND NOT SEVEN FLAGS. That rule used to be written out
 * once per way of leaving: one effect watched full screen turn off, another
 * watched the guide unmount, a third watched the floating window close, and
 * each decided for itself what should happen next. Every new way to leave was
 * a new effect somebody had to remember to write, and the ones nobody wrote
 * became these, all the same bug wearing different clothes:
 *
 *   - the corner player latching, which read as "full screen broke in the
 *     release build" (it had not — the bundles were byte-identical)
 *   - anything played after a minimise opening in the corner
 *   - Back out of the guide with the previewer running leaving a docked band
 *   - a nav press leaving that band on the page underneath
 *   - the guide unable to take the picture back out of the floating window
 *
 * Now there is one table. A state it cannot name is `"nowhere"`, which has a
 * single answer — playback ends — so a way of leaving that nobody thought
 * about resolves itself instead of leaving a video stranded on a browse page.
 */
export type PlayerPlacement =
  /** Nothing is playing. */
  | "none"
  /** Multiview draws its own grid; it owns the screen while it is open. */
  | "multiview"
  /** Filling the screen. */
  | "fullscreen"
  /** The guide's preview pane, in either EPG layout. */
  | "preview"
  /** Android's floating window, or the browser's. */
  | "pip"
  /** On its way to full screen, not yet there. Leave it be. */
  | "arriving"
  /**
   * Something is playing and has nowhere to be. Never a resting state: the
   * player resolves it at once, which is the whole point of naming it.
   */
  | "nowhere";

export interface PlacementInputs {
  /** Something is loaded in the player. */
  playing: boolean;
  /** The multiview grid is open. */
  multiview: boolean;
  /** The surface fills the screen (browser full screen, or the APK's own). */
  fullscreen: boolean;
  /** A floating window is showing the picture. */
  pip: boolean;
  /** A screen with a preview pane is mounted — the guide, in either layout. */
  previewHost: boolean;
  /**
   * Full screen has been asked for and has not landed yet.
   *
   * A play that fills the screen does it in two steps: the store records the
   * request, and an effect serves it. On the website the browser only confirms
   * it later, on `fullscreenchange`. In between, a video that is perfectly
   * fine has no home YET — and the first version of this table called that
   * "nowhere" and stopped it, so pressing Resume on a title card killed
   * playback the instant it started (measured on the projector, 2026-09-22).
   * Arriving somewhere is not the same as being nowhere.
   */
  arriving: boolean;
}

/**
 * Where the picture is, given what is true right now.
 *
 * Ordered by what wins. Multiview draws its own grid, so it comes first. Full
 * screen beats the preview pane, because entering it from the guide is the
 * viewer committing to what they were previewing. The floating window is only
 * a home when nothing larger is.
 */
export function placementOf(inputs: PlacementInputs): PlayerPlacement {
  if (!inputs.playing) return "none";
  if (inputs.multiview) return "multiview";
  if (inputs.fullscreen) return "fullscreen";
  if (inputs.pip) return "pip";
  if (inputs.arriving) return "arriving";
  if (inputs.previewHost) return "preview";
  return "nowhere";
}

/** What the player must do about a placement, if anything. */
export type PlacementAction =
  /** Leave it alone. */
  | "rest"
  /** Hand the picture to a floating window. */
  | "float"
  /** Nowhere to put it: playback ends. */
  | "stop"
  /** Coming out of the floating window with nothing else claiming it. */
  | "fill";

/**
 * What to do about being `"nowhere"`.
 *
 * It stops. Always, on every device.
 *
 * THIS USED TO FLOAT, and the rule it came from was the owner's, 2026-09-20:
 * "pip if available, or just exit to the previous screen with no playback at
 * all if not". That reads as a free choice between two homes, and on the
 * projector it behaved like one — the projector cannot do picture-in-picture,
 * so it always took the second branch and always looked right.
 *
 * On a phone that CAN, the first branch showed what it really costs. Android's
 * picture-in-picture is not a window the app owns; it is this activity shrunk
 * into a corner, which means the app is no longer the thing on screen. So Back
 * out of full screen on a Galaxy A15 left the Movies page for about a frame
 * and then handed the screen to the launcher (measured 2026-09-22). The owner
 * had asked for the opposite in the same breath — "exiting full screen when
 * playing a movie from the movies page it goes to the home screen instead of
 * movies" — and on a picture-in-picture device those two cannot both be had.
 * Stepping back INSIDE the app must keep you inside it.
 *
 * So automatic floating is gone, and picture-in-picture is now only ever
 * reached deliberately (owner, 2026-09-22: "pip isn't automatic unless leaving
 * app from root"):
 *
 *   the ⧉ button, or the `f` key      -> PlayerSurface's togglePip
 *   Back at the root, video playing   -> MainActivity, on the way out
 *   Home / recents with video playing -> onUserLeaveHint, on the way out
 *
 * Every one of those is the viewer choosing to leave, which is the only time
 * a floating window is what they meant. Nothing in this table asks for one.
 */
export function resolveNowhere(): PlacementAction {
  return "stop";
}

/**
 * What to do when the floating window closes with something still playing.
 *
 * The owner, 2026-09-20: "pip should expand to full screen". The guide is the
 * exception — it has a pane of its own to put the picture back into, and
 * arriving there is meant to reclaim it, not to fill the screen.
 */
export function afterPipCloses(playing: boolean, previewHost: boolean): PlacementAction {
  if (!playing) return "rest";
  return previewHost ? "rest" : "fill";
}
